import { client, dbClient } from './awsClients';
import { AdminGetUserCommand, InitiateAuthCommand } from "@aws-sdk/client-cognito-identity-provider";
import { fetchUserAttributes } from 'aws-amplify/auth';
import { UpdateItemCommand, UpdateItemCommandInput } from '@aws-sdk/client-dynamodb';
import awsConfig from '../amplifyconfiguration.json';
import { NEW_PASSWORD_REQUIRED, USER_PASSWORD_AUTH, HOSPITAL, NURSE } from '@constants/constants';
import { generateUniqueTimestamp } from './sharedUtils';
import { getDataUsingTenantId, getDataUsingUserId } from './sharedUtils';
import { HOSPITALS_TABLE, USER_INFO_TABLE } from '@constants/dynamoDBTableName';
import { updateUser, clearLocalStoragePreserveLanguage } from './utils';
import { setSessionStartTime } from './authSession';
import { API_KEY } from '@constants/AppConfig';
import { NEW_PASSWORD_API } from '@constants/auth/Api';

// const formattedDateTime = new Date().toISOString();
const formattedDateTime = generateUniqueTimestamp();

export async function userSession(email: string, password: string) {
  try {

    const command = new InitiateAuthCommand({
      AuthFlow: USER_PASSWORD_AUTH,
      ClientId: awsConfig.aws_user_pools_web_client_id,
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password,
      },
    });

    const response = await client.send(command);

    return response;
  } catch (error: any) {
    throw error;
  }
};

export async function userNewPassword(email: string, password: string, session: string) {
  try {
    const response = await fetch(NEW_PASSWORD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify({
        session: session,
        challengeName: NEW_PASSWORD_REQUIRED,
        email: email,
        password: password
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error: any) {
    throw error;
  }
};

export async function userStatusUpdate(tableName: string, id: string, tenant_id: string) {
  const attributeNames = {
    '#first_login': 'first_login',
    '#modified_by': 'modified_by',
    '#modified_at': 'modified_at',
};

const attributeValues = {
    ':first_login': { BOOL: false },
    ':modified_by': { S: tenant_id },
    ':modified_at': { S: formattedDateTime },
};

const updateExpression =
                'SET #first_login = :first_login, #modified_by = :modified_by, #modified_at = :modified_at';

const input: UpdateItemCommandInput = {
  TableName: tableName, // Replace with your table name
  Key: {
    uuid: { S: id }, // Replace with your primary key
  },
  ExpressionAttributeNames: attributeNames,
  ExpressionAttributeValues: attributeValues,
  UpdateExpression: updateExpression,
  ReturnValues: "ALL_NEW"
};

const command = new UpdateItemCommand(input);

try {
  const response = await dbClient.send(command);
} catch (error: any) {
    console.error("Error updating item:", error);

    let errorMessage: string;

    switch (error.name) {
        case 'ConditionalCheckFailedException':
            errorMessage = 'Condition check failed';
            break;
        case 'ProvisionedThroughputExceededException':
            errorMessage = 'Throughput limit exceeded';
            break;
        case 'ResourceNotFoundException':
            errorMessage = 'Requested resource not found';
            break;
        case 'ItemCollectionSizeLimitExceededException':
            errorMessage = 'Item collection size limit exceeded';
            break;
        case 'TransactionConflictException':
            errorMessage = 'Transaction conflict occurred';
            break;
        case 'RequestLimitExceeded':
            errorMessage = 'Request limit exceeded';
            break;
        case 'InternalServerError':
            errorMessage = 'Internal server error occurred';
            break;
        default:
            errorMessage = `Unknown error: ${error.message}`;
    }

    throw errorMessage
}
};

export async function getUserFromCognito (email: string) {
  try {
    const attributes = await fetchUserAttributes();
    const userAttributes = Object.entries(attributes).map(([key, value]) => ({
      Name: key,
      Value: value
    }));
    return { UserAttributes: userAttributes };
  }
  catch (error: any) {
    let errorMessage = 'An unexpected error occurred while fetching user data';

    switch (error.name) {
      case 'UserNotFoundException':
        errorMessage = "Invalid username or password.";
        break;
      case 'UserNotConfirmedException':
        errorMessage = "User not verified.";
        break;
      case 'PasswordResetRequiredException':
        errorMessage = "Password reset required for this user.";
        break;
      case 'TooManyRequestsException':
      case 'RequestLimitExceededException':
      case 'ProvisionedThroughputExceededException':
        errorMessage = "Too many requests. Please try again later.";
        break;
      case 'InvalidParameterException':
        errorMessage = "Invalid parameters provided.";
        break;
      case 'InternalServerError':
        errorMessage = "Internal server error occurred.";
        break;
      case 'ResourceNotFoundException':
        errorMessage = "Requested resource not found.";
        break;
      default:
        errorMessage = error.message;
        break;
    }

    throw errorMessage;
  }
};

export async function processUserDataAndValidate(
  email: string,
  pushNotificationToken: string,
  dispatch: any,
  navigate: any,
  HOME_PATH: string,
  setLoggedInUserRole: any,
  setLoggedInUserStatus: any,
  setHospitalBlocked: any
): Promise<string> {
  let userRole = '';

  const user = await getUserFromCognito(email);

  if (user && user.UserAttributes) {
    setSessionStartTime();
    const attributes = user.UserAttributes;
    const tenantId = attributes
      ? attributes.find(
          (attr: any) => attr.Name === 'custom:tenant_id'
        )?.Value || ''
      : '';
    const currentUserId = attributes
      ? attributes.find((attr: any) => attr.Name === 'sub')
          ?.Value || ''
      : '';
    userRole = attributes
      ? attributes.find(
          (attr: any) => attr.Name === 'custom:role'
        )?.Value || ''
      : '';

    let userInfo;

    if (pushNotificationToken != '') {
      let userInfoData = await getDataUsingUserId(
        USER_INFO_TABLE,
        currentUserId
      );

      if (userInfoData && userInfoData.Items && userInfoData.Items?.length != 0) {
        let userInfoId = userInfoData.Items[0];

        let attributeNames = {
          '#fcm_token': 'fcm_token',
          '#web': 'web',
          '#modified_by': 'modified_by',
          '#modified_at': 'modified_at'
        };

        let attributeValues = {
          ':web_token': { S: pushNotificationToken },
          ':modified_by': { S: currentUserId },
          ':modified_at': { S: generateUniqueTimestamp() },
        };

        let updateExpression =
          'SET #fcm_token.#web = :web_token, #modified_by = :modified_by, #modified_at = :modified_at';

        if (userInfoId?.uuid?.S) {
          await updateUser(USER_INFO_TABLE, userInfoId?.uuid?.S, attributeNames, attributeValues, updateExpression);
        }
      }
    }

    if (userRole.toLowerCase() == HOSPITAL.toLowerCase()) {
      userInfo = await getDataUsingTenantId(
        HOSPITALS_TABLE,
        tenantId
      );
    } else if (userRole.toLowerCase() == NURSE.toLowerCase()) {
      const hospitalData = await getDataUsingTenantId(
        HOSPITALS_TABLE,
        tenantId
      );

      if (
        hospitalData &&
        hospitalData.Items &&
        hospitalData.Items?.length != 0
      ) {
        const res = hospitalData.Items[0];
        const status = res.status?.S;
        if (
          status &&
          status.toLowerCase() != 'approved' &&
          status.toLowerCase() != 'verified'
        ) {
          clearLocalStoragePreserveLanguage();
          dispatch(setHospitalBlocked(true));
          dispatch(setLoggedInUserRole(userRole));
          dispatch(setLoggedInUserStatus(status));
          navigate(HOME_PATH);
          return userRole;
        }
      }
    } else {
      userInfo = await getDataUsingUserId(
        USER_INFO_TABLE,
        currentUserId
      );
    }

    if (userInfo && userInfo.Items && userInfo.Items?.length != 0) {
      const res = userInfo.Items[0];
      const status = res.status?.S;
      if (
        status &&
        status.toLowerCase() != 'approved' &&
        status.toLowerCase() != 'verified'
      ) {
        clearLocalStoragePreserveLanguage();
        dispatch(setLoggedInUserRole(userRole));
        dispatch(setLoggedInUserStatus(status));
        navigate(HOME_PATH);
        return userRole;
      }
    }
  }

  return userRole;
}